import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetUsersDetailByRoleMutation } from "../../../../../../../../../../api/services/user/userApi";
import {
  FormController,
  MuiButtonSecondary,
  MuiGrid,
  MuiSelect,
  MuiTypography,
  OptionsDataType,
  WarningConfirmationModal,
} from "../../../../../../../../../../components";
import { ROLE_IDS } from "../../../../../../../../../../helpers/constants";
import { UserDetails } from "../../../../../../../../../../helpers/types/common";
import {
  isNotEmpty,
  mapDropdownDataWithName,
} from "../../../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../../../redux/slices/appState/appStateSlice";
import { AddFacilitatorFormKeys, FacilitatorDetails } from "../../model";
import { facilitatorSiteCoreKeys } from "../../SiteCoreKeys";
import { addFacilitatorSchema } from "../../ValidationSchema";

interface AddFacilitatorProps {
  onAddFacilitatorCallback: (
    selectedFunction: OptionsDataType,
    selectedFacilitator: UserDetails,
  ) => void;
  facilitatorList: FacilitatorDetails[];
}

export const AddFacilitator = ({
  onAddFacilitatorCallback,
  facilitatorList,
}: AddFacilitatorProps) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const {
    fetchLabel,
    commonLabels,
    commonValidationMessages,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [functionFilteredOptions, setFunctionFilteredOptions] = useState<
    OptionsDataType[]
  >([]);
  const [
    showFacilitatorAlreadyAddedModal,
    setShowFacilitatorAlreadyAddedModal,
  ] = useState(false);
  const dispatch = useDispatch();
  const {
    data: facilitatorData,
    isPending: isGetFacilitatorLoading,
    isError: isGetFacilitatorError,
    mutateAsync: getFacilitatorByRoleId,
  } = useGetUsersDetailByRoleMutation();

  const isLoading = isGetFacilitatorLoading;
  const isError = isGetFacilitatorError;
  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  useEffect(() => {
    if (isError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isError]);
  const getLabelByKey = (key: string) =>
    fetchLabel(facilitatorSiteCoreKeys.facilitator, key);

  const formattedFunctionsOptions = fetchDropdownValues(
    facilitatorSiteCoreKeys.facilitator,
    facilitatorSiteCoreKeys.functionDropdownValue,
  );

  useEffect(() => {
    if (isNotEmpty(formattedFunctionsOptions))
      setFunctionFilteredOptions(formattedFunctionsOptions);
  }, [formattedFunctionsOptions?.length]);

  const methods = useForm({
    mode: "all",
    defaultValues: {
      [AddFacilitatorFormKeys.function]: "",
      [AddFacilitatorFormKeys.facilitator]: "",
    },
    resolver: yupResolver(addFacilitatorSchema(commonValidationMessages)),
  });

  const { reset, control, handleSubmit, setError, clearErrors } = methods;

  useEffect(() => {
    getFacilitatorByRoleId({ roleIds: [ROLE_IDS.deliveryCoach] });
  }, []);

  const facilitatorDropdownValue: OptionsDataType[] = useMemo(() => {
    if (isNotEmpty(facilitatorData)) {
      return mapDropdownDataWithName(facilitatorData);
    } else {
      return [];
    }
  }, [facilitatorData]);

  useEffect(() => {
    if (isNotEmpty(formattedFunctionsOptions)) {
      if (isNotEmpty(facilitatorList)) {
        const assignedFunctionIds = facilitatorList.map(
          (item) => item.functionId,
        );
        const filteredOptions = formattedFunctionsOptions.filter(
          (option) => !assignedFunctionIds.includes(option.value as string),
        );
        setFunctionFilteredOptions(filteredOptions);
      } else {
        setFunctionFilteredOptions(formattedFunctionsOptions);
      }
    }
  }, [facilitatorList?.length, formattedFunctionsOptions?.length]);

  const formSubmit = async (formData: unknown) => {
    const isFacilitatorAlreadyAdded = facilitatorList.find(
      (facilitator) =>
        facilitator.facilitatorId ===
        (formData as { facilitator: string }).facilitator,
    );
    if (isFacilitatorAlreadyAdded) {
      setError(AddFacilitatorFormKeys.facilitator, {
        type: "custom",
        message: getLabelByKey(
          facilitatorSiteCoreKeys.facilitatorAlreadyAssigned,
        ),
      });
      return;
    }

    const functionId = (formData as { function: string }).function;
    const selectedFunction = {
      label: fetchValueByKeyOfDropdown(
        facilitatorSiteCoreKeys.facilitator,
        facilitatorSiteCoreKeys.functionDropdownValue,
        functionId,
      ),
      value: functionId,
    } as OptionsDataType;
    const selectedFacilitator = facilitatorData?.find(
      (facilitator: UserDetails) =>
        facilitator.id === (formData as { facilitator: string }).facilitator,
    ) as UserDetails;
    onAddFacilitatorCallback(selectedFunction, selectedFacilitator);
    reset();
  };

  const handleFacilitatorAlreadyAddedModalClose = () =>
    setShowFacilitatorAlreadyAddedModal(false);

  const onFacilitatorChange = () =>
    clearErrors(AddFacilitatorFormKeys.facilitator);

  return (
    <>
      <Box className="add-function-header">
        <MuiTypography variant="h3Bold">
          {getLabelByKey(facilitatorSiteCoreKeys.facilitatorAddFunction)}
        </MuiTypography>
      </Box>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(formSubmit)}>
          <MuiGrid container className="add-function-container">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={AddFacilitatorFormKeys.function}
              >
                <MuiSelect
                  label={getLabelByKey(
                    facilitatorSiteCoreKeys.facilitatorFunctionDropDownLabel,
                  )}
                  options={functionFilteredOptions}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={AddFacilitatorFormKeys.facilitator}
              >
                <MuiSelect
                  label={getLabelByKey(
                    facilitatorSiteCoreKeys.facilitatorLabel,
                  )}
                  options={facilitatorDropdownValue}
                  visibleDefaultSelectOption
                  showSearch
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  onChange={onFacilitatorChange}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={classNames(gridSizeMediumClass, "add-function-button")}
            >
              <MuiButtonSecondary type="submit" id={`add-function-btn-cancel`}>
                {commonLabels.add}
              </MuiButtonSecondary>
            </MuiGrid>
          </MuiGrid>
        </form>
      </FormProvider>
      {showFacilitatorAlreadyAddedModal && (
        <WarningConfirmationModal
          show={showFacilitatorAlreadyAddedModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleFacilitatorAlreadyAddedModalClose}
          onPrimaryButtonClick={handleFacilitatorAlreadyAddedModalClose}
          labelContent={"AlreadyAdded "}
          showSecondaryButton={false}
        />
      )}
    </>
  );
};
